import React from 'react';
import { X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';

export interface ActionModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  submitVariant?: 'primary' | 'danger' | 'warning' | 'success';
  isSubmitting?: boolean;
  submitError?: string | null;
  canSubmit?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}

const VARIANT_BUTTON: Record<NonNullable<ActionModalProps['submitVariant']>, string> = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
  success: 'bg-green-600 hover:bg-green-700 text-white',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  warning: 'bg-amber-600 hover:bg-amber-700 text-white',
};

/**
 * Modal base reutilizável para as ações de desfecho + transfer +
 * approve-margin (US-COM-004 grupo 15.2). Cada modal específico
 * (Win/Lose/…) monta o próprio body como children e delega o footer,
 * loading, erro e o fechamento ao overlay comum.
 *
 * Backdrop click fecha, ESC fecha, submit dispara `onSubmit()`. O
 * modal fica em `role="dialog"` com `aria-modal` — acessível.
 */
export const ActionModal: React.FC<ActionModalProps> = ({
  isOpen,
  title,
  description,
  submitLabel,
  submitVariant = 'primary',
  isSubmitting,
  submitError,
  canSubmit = true,
  onClose,
  onSubmit,
  children,
}) => {
  // Enquanto uma submissão está em curso ignora backdrop-click, ESC e o X —
  // sem isso, o usuário podia fechar o modal com a mutation ainda pendente
  // e ver a UI num estado ambíguo (o request ia continuar, mas sem callback
  // de sucesso/erro visível). Também previne race de duplo-clique via Enter.
  const safeClose = React.useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') safeClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, safeClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="action-modal-title"
      aria-busy={isSubmitting || undefined}
      onClick={safeClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h2 id="action-modal-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-gray-600">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={safeClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {submitError && (
            <Alert variant="error" className="mb-3">
              {submitError}
            </Alert>
          )}
          {children}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={safeClose}
            disabled={isSubmitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}
            aria-busy={isSubmitting || undefined}
            className={`rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${VARIANT_BUTTON[submitVariant]}`}
          >
            {isSubmitting ? 'Processando…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
